<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { computed, inject } from 'vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import useEmitter from '~/common/composables/useEmitter';
import { useAnnotationsEdit } from '~/plans/composables/useAnnotationsEdit';
import { useDocumentTools } from '~/plans/composables/useDocumentTools';
import { TOOL_EVENTS } from '~/plans/constants';

const props = defineProps({
  active_tool: {
    type: String,
  },
  is_fullscreen_mode: {
    type: Boolean,
    default: false,
  },
  show_annotation_edit: {
    type: Boolean,
    default: false,
  },
  element_uid: {
    type: String,
  },
  version: {
    type: String,
  },
  text_annots_selected: {
    type: Boolean,
    default: false,
  },
  no_tools: {
    type: Boolean,
    default: false,
  },
});

const { $t } = useCommonImports();

const document_viewer_instance = inject('document-viewer-instance');
const emitter = useEmitter();

const {
  edit_text_annotation_tools,
  edit_normal_annotation_tools,
} = useAnnotationsEdit(document_viewer_instance);

const {
  undo,
  redo,
  delete_annotations,
} = useDocumentTools(document_viewer_instance);

const edit_text_annotation_tools_with_delete = computed(() => {
  return [
    ...(props.text_annots_selected?.length ? edit_text_annotation_tools.value : edit_normal_annotation_tools.value),
    {
      tooltip: $t('Delete'),
      icon: IconHawkTrashThree,
      event: TOOL_EVENTS.DELETE,
      action: () => delete_annotations(document_viewer_instance.value.getAnnotationManager().getSelectedAnnotations()),
    },
  ];
});

const drawing_tools = computed(() => {
  return [
    {
      tooltip: $t('Pan'),
      icon: IconHawkHandCopy,
      event: TOOL_EVENTS.PAN,
      action: () => emitter.emit('toolActivated', TOOL_EVENTS.PAN),
    },
    {
      tooltip: $t('Edit'),
      icon: IconHawkNavigationPointerOne,
      event: TOOL_EVENTS.EDIT,
      action: () => emitter.emit('toolActivated', TOOL_EVENTS.EDIT),
    },
    {
      tooltip: $t('Text'),
      icon: IconHawkTypeOne,
      event: TOOL_EVENTS.CREATE_TEXT,
      action: () => {
        emitter.emit('toolActivated', TOOL_EVENTS.CREATE_TEXT);
      },
    },
    {
      tooltip: $t('Highlight'),
      icon: IconHawkMagicWandOne,
      event: TOOL_EVENTS.CREATE_HIGHLIGHT,
      action: () => {
        emitter.emit('toolActivated', TOOL_EVENTS.CREATE_HIGHLIGHT);
      },
    },
    {
      tooltip: $t('Arrow'),
      icon: IconHawkArrowDownRight,
      event: TOOL_EVENTS.CREATE_ARROW,
      action: () => {
        emitter.emit('toolActivated', TOOL_EVENTS.CREATE_ARROW);
      },
    },
    {
      tooltip: $t('Line'),
      icon: IconHawkSlashDivider,
      event: TOOL_EVENTS.CREATE_LINE,
      action: () => {
        emitter.emit('toolActivated', TOOL_EVENTS.CREATE_LINE);
      },
    },
    {
      tooltip: $t('Cloud'),
      icon: IconHawkCloudOne,
      event: TOOL_EVENTS.CREATE_CLOUD,
      action: () => {
        emitter.emit('toolActivated', TOOL_EVENTS.CREATE_CLOUD);
      },
    },
    {
      tooltip: $t('Rectangle'),
      icon: IconHawkSquare,
      event: TOOL_EVENTS.CREATE_RECTANGLE,
      action: () => {
        emitter.emit('toolActivated', TOOL_EVENTS.CREATE_RECTANGLE);
      },
    },
    {
      tooltip: $t('Ellipse'),
      icon: IconHawkCircle,
      event: TOOL_EVENTS.CREATE_ELLIPSE,
      action: () => {
        emitter.emit('toolActivated', TOOL_EVENTS.CREATE_ELLIPSE);
      },
    },
    {
      tooltip: $t('Undo'),
      icon: IconHawkReverseLeft,
      event: TOOL_EVENTS.UNDO,
      action: () => {
        undo();
      },
    },
    {
      tooltip: $t('Redo'),
      icon: IconHawkReverseRight,
      event: TOOL_EVENTS.REDO,
      action: () => {
        redo();
      },
    },
  ];
});
</script>

<template>
  <div v-if="!no_tools" class="z-1 absolute left-4 top-4 flex flex-col gap-2">
    <template v-if="show_annotation_edit">
      <sheet-tool-icons :tools="edit_text_annotation_tools_with_delete" :active_tool="active_tool" :horizontal_icons="true" :sub_tool_options="{ align: 'left' }" />
    </template>
    <template v-else>
      <sheet-tool-icons :tools="drawing_tools" :active_tool="active_tool" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.compare-widget {
  width: calc(100% - (2rem + 3.125rem));
}
</style>
